import { useState, useEffect, useRef } from 'react';

/**
 * useVisibilitySentinel - Hook to track component visibility and page visibility.
 * Can be used to pause animations when off-screen or in background.
 * 
 * Uses hysteresis: immediately sets visible=true, but delays visible=false by 200ms
 * to prevent rapid toggling at viewport edges during scroll (which causes render storms).
 */
export function useVisibilitySentinel(elementRef: React.RefObject<Element>) {
    const [isVisible, setIsVisible] = useState(false);
    const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsTabVisible(!document.hidden);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        let observer: IntersectionObserver | null = null;
        if (elementRef.current) {
            observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        // Immediately mark visible (no delay for showing)
                        if (hideTimeoutRef.current) {
                            clearTimeout(hideTimeoutRef.current);
                            hideTimeoutRef.current = null;
                        }
                        setIsVisible(true);
                    } else {
                        // Delay hiding by 200ms to prevent rapid toggling at viewport edge
                        hideTimeoutRef.current = setTimeout(() => {
                            setIsVisible(false);
                            hideTimeoutRef.current = null;
                        }, 200);
                    }
                },
                { threshold: 0.01 } // Trigger as soon as 1% is visible
            );
            observer.observe(elementRef.current);
        }

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
            if (observer) {
                observer.disconnect();
            }
        };
    }, [elementRef]);

    return {
        isVisible: isVisible && isTabVisible,
        isTabVisible
    };
}
